// Cart Upsell Handler - Manages collection condition updates and Flickity reinitialization
class CartUpsellHandler {
  constructor() {
    this.isReinitializing = false; // Flag to prevent infinite loops
    this.lastReinitializeTime = 0; // For debouncing reinitializations
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Listen for cart updates from the existing product-form using pubsub
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      this.handleCartUpdate.bind(this)
    );

    // Also listen for cart drawer opened event to reinitialize
    document.addEventListener(
      "cart-drawer:opened",
      this.handleCartDrawerOpened.bind(this)
    );

    // Listen for cart drawer content updates
    document.addEventListener(
      "cart-drawer-content-updated",
      this.handleCartDrawerContentUpdated.bind(this)
    );

    // Set up a MutationObserver to watch for DOM changes in the cart drawer
    this.setupMutationObserver();
  }

  async handleCartUpdate(event) {
    try {
      // Wait for cart drawer to finish updating
      await this.waitForCartDrawerUpdate();

      // Check and update collection conditions
      await this.updateUpsellSection();

      // Always reinitialize Flickity after any cart update
      await this.reinitializeFlickity();
    } catch (error) {
      console.error("CartUpsellHandler: Error in handleCartUpdate:", error);
    }
  }

  async handleCartDrawerOpened() {
    // Wait a bit for the drawer to fully open and content to be rendered
    await this.waitForDOMReady();
    await this.reinitializeFlickity();
  }

  async handleCartDrawerContentUpdated(event) {
    // Wait for the DOM to be fully updated, then reinitialize
    await this.waitForDOMReady();

    // Re-setup the MutationObserver since the cart drawer content was replaced
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    this.setupMutationObserver();

    // Reinitialize Flickity
    await this.reinitializeFlickity();
  }

  async waitForCartDrawerUpdate() {
    // Wait for the cart drawer to finish its update process
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 100; // Increase max attempts for slower updates

      const checkUpdate = () => {
        attempts++;
        const cartDrawer = document.querySelector("cart-drawer");

        if (cartDrawer && !cartDrawer.classList.contains("loading")) {
          resolve();
        } else if (attempts >= maxAttempts) {
          resolve();
        } else {
          requestAnimationFrame(checkUpdate);
        }
      };
      checkUpdate();
    });
  }

  async waitForDOMReady() {
    // Wait for DOM to be ready using requestAnimationFrame
    return new Promise((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  async updateUpsellSection() {
    try {
      // Get current cart items to determine collection conditions
      const cartResponse = await fetch(window.Shopify.routes.root + "cart.js");
      const cart = await cartResponse.json();

      // Check collection conditions based on product tags and types
      const hasRefill = cart.items.some(
        (item) =>
          item.product_type === "Refill" ||
          (item.tags && item.tags.includes("Refill")) ||
          (item.product &&
            item.product.tags &&
            item.product.tags.includes("Refill"))
      );

      const hasShell = cart.items.some(
        (item) =>
          item.product_type === "Shell" ||
          (item.tags && item.tags.includes("Shell")) ||
          (item.product &&
            item.product.tags &&
            item.product.tags.includes("Shell"))
      );

      // Update upsell section based on conditions
      await this.refreshUpsellSection(hasRefill, hasShell);
    } catch (error) {
      console.error("Error updating upsell section:", error);
    }
  }

  async refreshUpsellSection(hasRefill, hasShell) {
    try {
      // Get the cart drawer element
      const cartDrawer = document.querySelector("cart-drawer");
      if (!cartDrawer) return;

      // Fetch updated cart drawer content
      const response = await fetch(
        `${window.location.pathname}?section_id=cart-drawer`
      );
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      // Find the upsell section in the new content
      const newUpsellSection = doc.querySelector(
        ".cart-drawer__upsell-container"
      );
      const currentUpsellSection = cartDrawer.querySelector(
        ".cart-drawer__upsell-container"
      );

      if (newUpsellSection && currentUpsellSection) {
        // Replace the upsell section content
        currentUpsellSection.innerHTML = newUpsellSection.innerHTML;

        // Reinitialize any carousels or other components
        await this.reinitializeFlickity();
      }
    } catch (error) {
      console.error("Error refreshing upsell section:", error);
    }
  }

  setupMutationObserver() {
    // Watch for changes in the cart drawer
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer) {
      return;
    }

    this.mutationObserver = new MutationObserver(async (mutations) => {
      // Skip if we're currently reinitializing
      if (this.isReinitializing) {
        return;
      }

      let shouldReinitialize = false;

      mutations.forEach((mutation) => {
        // Only check for significant changes, not Flickity's internal changes
        if (mutation.type === "childList") {
          const target = mutation.target;

          // Check if cart items were added/removed (significant change)
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains("cart-item")) {
                shouldReinitialize = true;
              }
              // Check if upsell container was completely replaced
              if (
                node.classList &&
                node.classList.contains("cart-drawer__upsell-container")
              ) {
                shouldReinitialize = true;
              }
            }
          });

          mutation.removedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList && node.classList.contains("cart-item")) {
                shouldReinitialize = true;
              }
              // Check if upsell container was completely removed
              if (
                node.classList &&
                node.classList.contains("cart-drawer__upsell-container")
              ) {
                shouldReinitialize = true;
              }
            }
          });

          // Check if the target is the upsell container itself
          if (
            target.classList &&
            target.classList.contains("cart-drawer__upsell-container")
          ) {
            // Only reinitialize if it's a significant change (not just Flickity internal changes)
            const hasSignificantChange =
              mutation.addedNodes.length > 0 ||
              mutation.removedNodes.length > 0;
            if (hasSignificantChange) {
              shouldReinitialize = true;
            }
          }
        }
      });

      if (shouldReinitialize) {
        // Use async/await for debouncing instead of setTimeout
        await this.debouncedReinitialize();
      }
    });

    // Start observing with more specific configuration
    this.mutationObserver.observe(cartDrawer, {
      childList: true,
      subtree: true,
      attributes: false,
      characterData: false,
    });
  }

  async reinitializeFlickity() {
    // Prevent infinite loops
    if (this.isReinitializing) {
      return;
    }

    this.isReinitializing = true;

    try {
      // Wait for DOM to be ready, then reinitialize Flickity
      await this.waitForDOMReady();

      const upsellContents = document.querySelectorAll(
        ".cart-drawer__upsell-content"
      );

      if (upsellContents.length > 0) {
        // If Flickity is not available, wait for it to load
        if (typeof Flickity === "undefined") {
          await this.waitForFlickity();
        }

        if (typeof Flickity !== "undefined") {
          upsellContents.forEach((upsellContent, index) => {
            try {
              // Destroy existing instance if any
              const existingFlickity = Flickity.data(upsellContent);
              if (existingFlickity) {
                existingFlickity.destroy();
              }

              // Reinitialize with the same options
              new Flickity(upsellContent, {
                cellAlign: "center",
                contain: true,
                pageDots: true,
                prevNextButtons: true,
                adaptiveHeight: true,
              });
            } catch (error) {
              console.error(
                `Error reinitializing Flickity instance ${index}:`,
                error
              );
            }
          });
        }
      }
    } finally {
      // Reset the flag immediately after completion
      this.isReinitializing = false;
    }
  }

  async waitForFlickity() {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50; // Wait up to 5 seconds

      const checkFlickity = async () => {
        attempts++;
        if (typeof Flickity !== "undefined") {
          resolve();
        } else if (attempts >= maxAttempts) {
          resolve();
        } else {
          await this.waitForDOMReady();
          checkFlickity();
        }
      };
      checkFlickity();
    });
  }

  async debouncedReinitialize() {
    const now = Date.now();
    const debounceDelay = 150; // 150ms debounce

    // If we're already reinitializing, skip
    if (this.isReinitializing) {
      return;
    }

    // If not enough time has passed since last reinitialization, wait
    if (now - this.lastReinitializeTime < debounceDelay) {
      await this.waitForDOMReady();
      return this.debouncedReinitialize();
    }

    // Update the last reinitialize time
    this.lastReinitializeTime = now;

    // Perform the reinitialization
    await this.waitForDOMReady();
    await this.reinitializeFlickity();
  }

  disconnectedCallback() {
    // Clean up event listeners when element is removed
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    // Clean up MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  window.cartUpsellHandler = new CartUpsellHandler();
});

// Also initialize when cart drawer is opened
document.addEventListener("cart-drawer:opened", () => {
  // Always create a new instance when cart drawer opens
  if (window.cartUpsellHandler) {
    if (window.cartUpsellHandler.cartUpdateUnsubscriber) {
      window.cartUpsellHandler.cartUpdateUnsubscriber();
    }
    if (window.cartUpsellHandler.mutationObserver) {
      window.cartUpsellHandler.mutationObserver.disconnect();
    }
  }
  window.cartUpsellHandler = new CartUpsellHandler();
});

// Fallback initialization for cases where DOMContentLoaded might have already fired
if (document.readyState === "loading") {
  // DOM is still loading, wait for DOMContentLoaded
  document.addEventListener("DOMContentLoaded", () => {
    if (!window.cartUpsellHandler) {
      window.cartUpsellHandler = new CartUpsellHandler();
    }
  });
} else {
  // DOM is already loaded, initialize immediately
  if (!window.cartUpsellHandler) {
    window.cartUpsellHandler = new CartUpsellHandler();
  }
}
